/**
 * Zustand Store — Live Market Data.
 *
 * Manages watchlist instruments and their real-time tick data.
 * Syncs with the backend WebSocket feed and REST APIs.
 */

import { create } from 'zustand';
import { api } from '@/config/api';

export interface TickData {
  instrument_token: string;
  trading_symbol: string;
  exchange_segment: string;
  ltp: string;
  open: string;
  high: string;
  low: string;
  close: string;
  volume: string;
  change: string;
  change_percent: string;
  total_buy_qty?: string;
  total_sell_qty?: string;
  best_bid_price?: string;
  best_bid_qty?: string;
  best_ask_price?: string;
  best_ask_qty?: string;
  open_interest?: string;
  last_traded_qty?: string;
  /** Broker's last-trade timestamp, e.g. "07/09/2026 09:18:30". Old value + LTP = close → not traded today. */
  last_traded_time?: string;
  /** When our backend last received a tick for this token (ISO). Old → the feed is not delivering it. */
  updated_at?: string;
}

interface MarketState {
  // Watchlist instrument tokens → tick data
  ticks: Record<string, TickData>;
  watchlist: string[];
  isLoading: boolean;
  error: string | null;

  // Actions
  updateTick: (token: string, data: Partial<TickData>) => void;
  setWatchlist: (tokens: string[]) => void;
  addToWatchlist: (token: string, symbol?: string, segment?: string) => void;
  removeFromWatchlist: (token: string) => void;
  fetchWatchlist: () => Promise<void>;
  clearError: () => void;
}

export const useMarketStore = create<MarketState>((set, get) => ({
  ticks: {},
  watchlist: [],
  isLoading: false,
  error: null,

  updateTick: (token, data) => {
    set((state) => ({
      ticks: {
        ...state.ticks,
        [token]: {
          ...(state.ticks[token] || {
            instrument_token: token,
            trading_symbol: '',
            exchange_segment: '',
            ltp: '0',
            open: '0',
            high: '0',
            low: '0',
            close: '0',
            volume: '0',
            change: '0',
            change_percent: '0',
          }),
          ...data,
        },
      },
    }));
  },

  setWatchlist: (tokens) => set({ watchlist: tokens }),

  addToWatchlist: (token, symbol = '', segment = '') => {
    const { watchlist, ticks } = get();
    const newTicks = { ...ticks };
    if (!newTicks[token]) {
      newTicks[token] = {
        instrument_token: token,
        trading_symbol: symbol,
        exchange_segment: segment,
        ltp: '0',
        open: '0',
        high: '0',
        low: '0',
        close: '0',
        volume: '0',
        change: '0',
        change_percent: '0',
      };
    }
    if (!watchlist.includes(token)) {
      set({ watchlist: [...watchlist, token], ticks: newTicks });
    } else {
      set({ ticks: newTicks });
    }
  },

  removeFromWatchlist: (token) => {
    set((state) => ({
      watchlist: state.watchlist.filter((t) => t !== token),
      ticks: Object.fromEntries(
        Object.entries(state.ticks).filter(([k]) => k !== token)
      ),
    }));
  },

  fetchWatchlist: async () => {
    set({ isLoading: true, error: null });
    try {
      const { data } = await api.get('/market/watchlist');
      const tokens: string[] = [];
      const ticks: Record<string, TickData> = {};

      for (const item of data) {
        const token = item.instrument_token;
        tokens.push(token);
        ticks[token] = {
          instrument_token: token,
          trading_symbol: item.trading_symbol || '',
          exchange_segment: item.exchange_segment || '',
          ltp: item.ltp || '0',
          open: item.open || '0',
          high: item.high || '0',
          low: item.low || '0',
          close: item.close || '0',
          volume: item.volume || '0',
          change: item.change || '0',
          change_percent: item.change_percent || '0',
          open_interest: item.open_interest || '0',
        };
      }

      set({ watchlist: tokens, ticks, isLoading: false });
    } catch {
      set({ error: 'Failed to load watchlist', isLoading: false });
    }
  },

  clearError: () => set({ error: null }),
}));
